<template>
  <div class="flex flex-wrap">
    <div class="w-full px-4">
      <!-- Hero -->
      <div class="rounded-2xl shadow-xl bg-white overflow-hidden mb-6 border border-gray-100">
        <div class="bg-respina-primary px-6 py-7 text-white">
          <h1 class="text-2xl md:text-3xl font-bold mb-1">Text to Speech</h1>
          <p class="text-respina-light text-sm opacity-90 mb-4">
            Build a reusable voice-fragment library once, then stitch full flight
            announcements from it — no ElevenLabs calls needed to compose.
          </p>
          <div class="flex flex-wrap gap-2">
            <span class="inline-flex items-center gap-1.5 text-xs bg-white/15 rounded-full px-3 py-1">
              <i class="fas fa-user-circle"></i> Voice: Arabella
            </span>
            <span class="inline-flex items-center gap-1.5 text-xs bg-white/15 rounded-full px-3 py-1">
              <i class="fas fa-microchip"></i> Model: Eleven v3
            </span>
            <span class="inline-flex items-center gap-1.5 text-xs bg-white/15 rounded-full px-3 py-1">
              <i class="fas fa-sliders-h"></i> Stability: Natural (0.5)
            </span>
            <span class="inline-flex items-center gap-1.5 text-xs bg-white/15 rounded-full px-3 py-1">
              <i class="fas fa-file-audio"></i> MP3 44.1kHz 128kbps
            </span>
          </div>
        </div>

        <!-- Tabs -->
        <div class="flex border-b border-gray-100 bg-gray-50/60">
          <button
            v-for="t in tabs"
            :key="t.id"
            type="button"
            @click="tab = t.id"
            class="relative flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-colors"
            :class="tab === t.id ? 'text-respina-primary' : 'text-gray-500 hover:text-gray-700'"
          >
            <span
              class="flex items-center justify-center w-6 h-6 rounded-full text-xs"
              :class="tab === t.id ? 'bg-respina-primary text-white' : 'bg-gray-200 text-gray-600'"
            >{{ t.step }}</span>
            <i class="fas" :class="t.icon"></i>
            {{ t.label }}
            <span
              v-if="tab === t.id"
              class="absolute left-0 right-0 -bottom-px h-0.5 bg-respina-primary"
            ></span>
          </button>
        </div>
      </div>

      <!-- Global error -->
      <div
        v-if="error"
        class="mb-6 rounded-xl bg-red-50 border border-red-200 text-red-700 px-5 py-4 flex items-start justify-between gap-3"
      >
        <span class="flex-1"><i class="fas fa-exclamation-circle ml-2"></i>{{ error }}</span>
        <button type="button" class="text-red-500 hover:text-red-700 text-lg leading-none" @click="error = null">×</button>
      </div>

      <!-- ============================================================ -->
      <!-- TAB 1 — LIBRARY                                               -->
      <!-- ============================================================ -->
      <section v-show="tab === 'library'">
        <!-- Overall progress -->
        <div class="rounded-2xl shadow-lg bg-white border border-gray-100 p-6 md:p-8 mb-6">
          <div class="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i class="fas fa-layer-group text-respina-primary"></i>
                Fragment library
              </h2>
              <p class="text-sm text-gray-500 mt-1">
                Generate each reusable clip once (numbers, cities, …). Compose stitches these
                locally into announcements, so they must all exist first. Already-generated files are skipped.
              </p>
            </div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                @click="loadManifest"
                :disabled="loadingManifest || building"
                class="inline-flex items-center gap-2 px-3 h-10 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                <i class="fas" :class="loadingManifest ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                Refresh
              </button>
              <button
                v-if="!building"
                type="button"
                @click="buildMissing(null)"
                :disabled="!manifest || manifest.missing === 0"
                class="inline-flex items-center gap-2 px-5 h-10 text-sm font-semibold rounded-lg bg-respina-primary hover:bg-respina-secondary text-white shadow-md disabled:opacity-50"
              >
                <i class="fas fa-bolt"></i>
                Build all missing
              </button>
              <button
                v-else
                type="button"
                @click="stopFlag = true"
                class="inline-flex items-center gap-2 px-5 h-10 text-sm font-semibold rounded-lg bg-red-500 hover:bg-red-600 text-white shadow-md"
              >
                <i class="fas fa-stop"></i>
                Stop
              </button>
            </div>
          </div>

          <div v-if="manifest">
            <div class="flex items-end justify-between mb-2">
              <span class="text-sm font-semibold text-gray-700">
                {{ manifest.existing }} / {{ manifest.total }} fragments ready
              </span>
              <span class="text-sm font-bold" :class="manifest.missing === 0 ? 'text-green-600' : 'text-respina-primary'">
                {{ overallPct }}%
              </span>
            </div>
            <div class="h-3 rounded-full bg-gray-100 overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="manifest.missing === 0 ? 'bg-green-500' : 'bg-respina-primary'"
                :style="{ width: overallPct + '%' }"
              ></div>
            </div>
            <div class="flex items-center justify-between mt-2 text-xs text-gray-500">
              <span>{{ manifest.missing }} remaining</span>
              <span v-if="manifest.missing > 0">
                ≈ {{ missingChars.toLocaleString() }} chars · est. {{ estCost(missingChars) }}
              </span>
              <span v-else class="text-green-600 font-semibold">
                <i class="fas fa-check-circle"></i> Library complete
              </span>
            </div>
          </div>
          <div v-else-if="loadingManifest" class="py-8 text-center text-gray-400">
            <i class="fas fa-spinner fa-spin text-xl"></i>
          </div>
        </div>

        <!-- Build error (e.g. Free plan / 402) -->
        <div
          v-if="buildError"
          class="mb-6 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-5 py-4"
        >
          <div class="font-semibold flex items-center gap-2 mb-1">
            <i class="fas fa-triangle-exclamation"></i> Generation stopped
          </div>
          <p class="text-sm">{{ buildError }}</p>
          <p v-if="/free|payment|upgrade|402/i.test(buildError)" class="text-xs mt-2 text-amber-700">
            Your ElevenLabs account can't use library voices on the Free plan. Enable
            usage-based billing / a paid plan, then try again.
          </p>
        </div>

        <!-- Live build ticker -->
        <div
          v-if="building || buildStats"
          class="mb-6 rounded-xl bg-blue-50 border border-blue-100 px-5 py-4 text-sm"
        >
          <div class="flex items-center gap-2 text-blue-800 font-semibold mb-2">
            <i class="fas" :class="building ? 'fa-spinner fa-spin' : 'fa-flag-checkered'"></i>
            {{ building ? 'Generating…' : 'Last run finished' }}
          </div>
          <div class="grid grid-cols-3 gap-3 text-center">
            <div>
              <div class="text-xl font-bold text-green-600">{{ buildStats ? buildStats.created : 0 }}</div>
              <div class="text-xs text-gray-500">created</div>
            </div>
            <div>
              <div class="text-xl font-bold text-gray-700">{{ buildStats ? buildStats.remaining : 0 }}</div>
              <div class="text-xs text-gray-500">remaining</div>
            </div>
            <div>
              <div class="text-xl font-bold" :class="(buildStats && buildStats.failed) ? 'text-red-600' : 'text-gray-400'">
                {{ buildStats ? buildStats.failed : 0 }}
              </div>
              <div class="text-xs text-gray-500">failed</div>
            </div>
          </div>
          <div v-if="buildStats && buildStats.lastText" class="mt-3 text-center text-xs text-gray-500 truncate" dir="rtl">
            <i class="fas fa-volume-high ml-1"></i> {{ buildStats.lastText }}
          </div>
        </div>

        <!-- Per-category grid -->
        <div class="fl-grid">
          <div
            v-for="cat in categoryCards"
            :key="cat.id"
            class="fl-card"
            :class="{ 'is-done': cat.done }"
          >
            <button type="button" class="fl-card__head" @click="openFragments(cat)" :title="`Manage ${cat.label}`">
              <span class="fl-card__icon" :class="{ 'is-done': cat.done }">
                <i class="fas" :class="cat.icon"></i>
              </span>
              <span class="fl-card__meta">
                <span class="fl-card__label">{{ cat.label }}</span>
                <span class="fl-card__count">{{ cat.existing }} / {{ cat.total }} ready</span>
              </span>
              <span v-if="cat.done" class="fl-badge">Done</span>
              <i v-else class="fas fa-chevron-right fl-card__chev"></i>
            </button>

            <div class="fl-progress">
              <div class="fl-progress__bar" :class="{ 'is-done': cat.done }" :style="{ width: cat.pct + '%' }"></div>
            </div>

            <div class="fl-actions">
              <button
                type="button"
                class="fl-btn fl-btn--primary"
                @click="buildMissing([cat.id])"
                :disabled="building || cat.done"
              >
                <i class="fas" :class="cat.done ? 'fa-check' : 'fa-bolt'"></i>
                {{ cat.done ? 'Complete' : `Build ${cat.total - cat.existing}` }}
              </button>
              <button
                type="button"
                class="fl-btn fl-btn--ghost"
                @click="openFragments(cat)"
                title="View & edit each clip"
              >
                <i class="fas fa-sliders-h"></i>
                Manage
              </button>
            </div>
          </div>
        </div>

        <!-- Advanced -->
        <div class="mt-6">
          <button
            type="button"
            class="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1.5"
            @click="showAdvanced = !showAdvanced"
          >
            <i class="fas" :class="showAdvanced ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            Advanced (regenerate / overwrite)
          </button>
          <div v-if="showAdvanced" class="mt-3 rounded-xl bg-gray-50 border border-gray-100 p-4 text-sm">
            <label class="flex items-center gap-2 text-gray-600 mb-3">
              <input type="checkbox" v-model="overwrite" class="accent-respina-primary" />
              Overwrite existing files (re-generate, e.g. after editing a phrase)
            </label>
            <p class="text-xs text-gray-400">
              Overwrite runs a single pass (up to 50 files) — best for small categories like
              <code>phrases</code>, <code>words</code>, <code>months</code>. Use the category
              buttons above with this enabled.
            </p>
          </div>
        </div>
      </section>

      <!-- ============================================================ -->
      <!-- FRAGMENT LIST / EDIT MODAL (teleported to body)              -->
      <!-- ============================================================ -->
      <teleport to="body">
        <div v-if="fragModal" class="fm-overlay" @click.self="closeFragments">
          <div class="fm-panel" role="dialog" aria-modal="true" @keydown.esc="closeFragments">
            <!-- header -->
            <header class="fm-header">
              <span class="fm-header__icon"><i class="fas" :class="fragModal.icon"></i></span>
              <div class="fm-header__meta">
                <div class="fm-header__title">{{ fragModal.label }}</div>
                <div class="fm-header__sub">
                  <b>{{ fragReadyCount }}</b> / {{ fragItems.length }} ready
                  <span v-if="fragItems.length && fragItems.length - fragReadyCount > 0">
                    · {{ fragItems.length - fragReadyCount }} missing
                  </span>
                </div>
              </div>
              <button type="button" class="fm-close" @click="closeFragments" aria-label="Close">
                <i class="fas fa-times"></i>
              </button>
            </header>

            <!-- toolbar -->
            <div class="fm-toolbar">
              <div class="fm-search">
                <i class="fas fa-search"></i>
                <input v-model="fragFilter" type="text" placeholder="Filter by number or text…" />
              </div>
              <label class="fm-check">
                <input type="checkbox" v-model="fragOnlyMissing" />
                <span>Only missing</span>
              </label>
              <button
                v-if="fragCanAdd && !fragAddOpen"
                type="button"
                class="fm-addbtn"
                @click="openAdd"
              >
                <i class="fas fa-plus"></i> {{ fragAddIsCity ? 'افزودن شهر' : 'افزودن ایرلاین' }}
              </button>
            </div>

            <!-- add airline / city form -->
            <div v-if="fragAddOpen" class="fm-add">
              <div class="fm-add__row">
                <input v-model="fragAddName" class="fm-add__input" dir="rtl"
                  :placeholder="fragAddIsCity ? 'نام شهر (مثلاً کاشان)' : 'نام ایرلاین (مثلاً کاسپین)'" />
                <input v-if="!fragAddIsCity" v-model="fragAddSpoken" class="fm-add__input" dir="rtl"
                  placeholder="تلفظ با اعراب (اختیاری)" />
                <input v-else v-model="fragAddIata" class="fm-add__input" style="max-width:110px"
                  placeholder="IATA (اختیاری)" />
              </div>
              <div class="fm-add__actions">
                <button type="button" class="fm-btn fm-btn--primary" :disabled="fragAddBusy || !fragAddName.trim()" @click="saveAdd">
                  <i class="fas" :class="fragAddBusy ? 'fa-spinner fa-spin' : 'fa-check'"></i> افزودن
                </button>
                <button type="button" class="fm-btn" @click="cancelAdd">انصراف</button>
              </div>
            </div>

            <div v-if="fragError" class="fm-error">
              <span><i class="fas fa-exclamation-circle"></i> {{ fragError }}</span>
              <button type="button" @click="fragError = null" aria-label="Dismiss">×</button>
            </div>

            <!-- list -->
            <div class="fm-body">
              <div v-if="fragLoading" class="fm-state"><i class="fas fa-spinner fa-spin"></i></div>
              <div v-else-if="!filteredFrags.length" class="fm-state">No fragments match.</div>
              <ul v-else class="fm-list">
                <li v-for="it in visibleFrags" :key="it.key" class="fm-row" :class="{ 'is-missing': !it.exists }">
                  <span class="fm-dot" :class="it.exists ? 'is-on' : 'is-off'" :title="it.exists ? 'Ready' : 'Not generated'"></span>

                  <!-- edit mode -->
                  <template v-if="fragEditKey === it.key">
                    <div class="fm-row__text">
                      <textarea
                        v-model="fragEditText"
                        class="fm-edit"
                        dir="rtl"
                        rows="2"
                        @keydown.esc="cancelEdit"
                      ></textarea>
                    </div>
                    <div class="fm-row__actions">
                      <button
                        type="button"
                        class="fm-iconbtn fm-iconbtn--green"
                        :disabled="fragBusyKey === it.key || !fragEditText.trim()"
                        @click="saveEdit(it)"
                        title="Save text" aria-label="Save"
                      >
                        <i class="fas" :class="(fragBusyKey === it.key && fragBusyAction === 'save') ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                      </button>
                      <button type="button" class="fm-iconbtn" @click="cancelEdit" title="Cancel" aria-label="Cancel">
                        <i class="fas fa-xmark"></i>
                      </button>
                    </div>
                  </template>

                  <!-- normal mode -->
                  <template v-else>
                    <div class="fm-row__text">
                      <div class="fm-row__phrase" dir="rtl">
                        {{ it.text }}
                        <span v-if="it.custom" class="fm-tag fm-tag--new" title="آیتم افزوده‌شده">افزوده</span>
                        <span v-if="it.stale" class="fm-tag fm-tag--warn" title="متن ادیت شده — برای اعمال روی صدا Regenerate کن">✎ نیاز به ساخت</span>
                        <span v-else-if="it.overridden" class="fm-tag" title="این متن ادیت‌شده است (override)">ادیت‌شده</span>
                      </div>
                      <div class="fm-row__key">{{ it.key }}</div>
                    </div>
                    <div class="fm-row__actions">
                      <button
                        v-if="it.custom"
                        type="button"
                        class="fm-iconbtn fm-iconbtn--red"
                        :disabled="fragBusyKey === it.key"
                        @click="removeCustom(it)"
                        title="حذف کامل این آیتم" aria-label="Remove item"
                      >
                        <i class="fas" :class="(fragBusyKey === it.key && fragBusyAction === 'del') ? 'fa-spinner fa-spin' : 'fa-trash-can'"></i>
                      </button>
                      <button type="button" class="fm-iconbtn" @click="startEdit(it)" title="Edit text" aria-label="Edit text">
                        <i class="fas fa-pen"></i>
                      </button>
                      <button
                        v-if="it.overridden"
                        type="button"
                        class="fm-iconbtn"
                        :disabled="fragBusyKey === it.key"
                        @click="resetFrag(it)"
                        title="Reset to default text" aria-label="Reset to default"
                      >
                        <i class="fas" :class="(fragBusyKey === it.key && fragBusyAction === 'reset') ? 'fa-spinner fa-spin' : 'fa-rotate-left'"></i>
                      </button>
                      <button
                        v-if="it.exists"
                        type="button"
                        class="fm-iconbtn"
                        :class="{ 'is-active': fragPlayingKey === it.key }"
                        @click="playFrag(it)"
                        title="Play" aria-label="Play"
                      >
                        <i class="fas" :class="fragPlayingKey === it.key ? 'fa-circle-notch fa-spin' : 'fa-play'"></i>
                      </button>
                      <button
                        type="button"
                        class="fm-iconbtn"
                        :class="{ 'is-active': it.stale }"
                        :disabled="fragBusyKey === it.key"
                        @click="genFrag(it)"
                        :title="it.exists ? 'Regenerate (ElevenLabs)' : 'Generate (ElevenLabs)'"
                        :aria-label="it.exists ? 'Regenerate' : 'Generate'"
                      >
                        <i class="fas" :class="(fragBusyKey === it.key && fragBusyAction === 'gen') ? 'fa-spinner fa-spin' : (it.exists ? 'fa-rotate' : 'fa-bolt')"></i>
                      </button>
                      <button
                        type="button"
                        class="fm-iconbtn fm-iconbtn--blue"
                        :disabled="fragBusyKey === it.key"
                        @click="pickUpload(it)"
                        title="Replace with your own file" aria-label="Upload replacement"
                      >
                        <i class="fas" :class="(fragBusyKey === it.key && fragBusyAction === 'up') ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
                      </button>
                      <button
                        v-if="it.exists"
                        type="button"
                        class="fm-iconbtn fm-iconbtn--red"
                        :disabled="fragBusyKey === it.key"
                        @click="delFrag(it)"
                        title="Delete" aria-label="Delete"
                      >
                        <i class="fas" :class="(fragBusyKey === it.key && fragBusyAction === 'del') ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
                      </button>
                    </div>
                  </template>
                </li>
              </ul>

              <button
                v-if="!fragLoading && filteredFrags.length > visibleFrags.length"
                type="button"
                class="fm-loadmore"
                @click="fragVisible += 200"
              >
                Load more ({{ filteredFrags.length - visibleFrags.length }} more)
              </button>
            </div>

            <!-- footer -->
            <footer class="fm-footer">
              <span>Showing {{ visibleFrags.length }} of {{ filteredFrags.length }}</span>
              <button type="button" class="fm-btn" @click="closeFragments">Close</button>
            </footer>

            <!-- hidden file input for uploads -->
            <input ref="fragFile" type="file" accept="audio/mpeg,audio/mp3,.mp3" style="display:none" @change="onUploadPicked" />
          </div>
        </div>
      </teleport>

      <!-- ============================================================ -->
      <!-- TAB 2 — COMPOSE                                               -->
      <!-- ============================================================ -->
      <section v-show="tab === 'compose'">
        <div class="rounded-2xl shadow-lg bg-white border border-gray-100 p-6 md:p-8 mb-6">
          <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2 mb-1">
            <i class="fas fa-wand-magic-sparkles text-respina-primary"></i>
            Compose announcement
          </h2>
          <p class="text-sm text-gray-500 mb-6">
            Fill the variables — the full text updates live below. Then build one MP3
            stitched 100% locally from your library fragments (silence-trimmed &
            volume-levelled). <span class="text-green-600 font-medium">No ElevenLabs cost.</span>
          </p>

          <div class="tts-2col">
            <!-- Cancelled flight -->
            <div class="rounded-xl border border-gray-100 p-4">
              <div class="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-2">
                <i class="fas fa-ban text-red-400"></i> Cancelled flight
              </div>
              <div class="space-y-3">
                <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Flight number <span class="text-red-400">*</span></label>
                  <input type="number" min="10" max="9999" v-model.number="form.flightNumber" class="tts-input" />
                </div>
                <div class="tts-pair">
                  <div>
                    <label class="block text-xs font-semibold text-gray-600 mb-1">Day <span class="text-red-400">*</span></label>
                    <select v-model.number="form.day" class="tts-input">
                      <option v-for="d in 31" :key="d" :value="d">{{ d }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs font-semibold text-gray-600 mb-1">Month <span class="text-red-400">*</span></label>
                    <select v-model.number="form.month" class="tts-input" dir="rtl">
                      <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Origin <span class="text-red-400">*</span></label>
                  <select v-model="form.origin" class="tts-input" dir="rtl">
                    <option v-for="c in cityOptions" :key="c.key" :value="c.key">{{ c.text }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Destination <span class="text-red-400">*</span></label>
                  <select v-model="form.destination" class="tts-input" dir="rtl">
                    <option v-for="c in cityOptions" :key="c.key" :value="c.key">{{ c.text }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Airline <span class="text-red-400">*</span></label>
                  <select v-model="form.airline" class="tts-input" dir="rtl">
                    <option v-for="a in airlineOptions" :key="a.key" :value="a.key">{{ a.text }}</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Replacement flight -->
            <div class="rounded-xl border border-gray-100 p-4">
              <div class="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-2">
                <i class="fas fa-plane-departure text-green-500"></i> Replacement flight
              </div>
              <div class="space-y-3">
                <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Flight number <span class="text-red-400">*</span></label>
                  <input type="number" min="10" max="9999" v-model.number="form.rFlight" class="tts-input" />
                </div>
                <div class="tts-pair">
                  <div>
                    <label class="block text-xs font-semibold text-gray-600 mb-1">Day <span class="text-red-400">*</span></label>
                    <select v-model.number="form.rDay" class="tts-input">
                      <option v-for="d in 31" :key="d" :value="d">{{ d }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs font-semibold text-gray-600 mb-1">Month <span class="text-red-400">*</span></label>
                    <select v-model.number="form.rMonth" class="tts-input" dir="rtl">
                      <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Airline <span class="text-red-400">*</span></label>
                  <select v-model="form.rAirline" class="tts-input" dir="rtl">
                    <option v-for="a in airlineOptions" :key="a.key" :value="a.key">{{ a.text }}</option>
                  </select>
                </div>
                <div class="tts-pair">
                  <div>
                    <label class="block text-xs font-semibold text-gray-600 mb-1">Hour (24h) <span class="text-red-400">*</span></label>
                    <input type="number" min="0" max="23" v-model.number="form.hour" class="tts-input" />
                  </div>
                  <div>
                    <label class="block text-xs font-semibold text-gray-600 mb-1">Minute <span class="text-red-400">*</span></label>
                    <input type="number" min="0" max="59" v-model.number="form.minute" class="tts-input" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Options -->
          <div class="mt-4">
            <label class="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input type="checkbox" v-model="form.operator" class="accent-respina-primary w-4 h-4" />
              <span>
                <i class="fas fa-headset text-gray-400 ml-1"></i>
                جمله‌ی اپراتور را بگوید
                <span class="text-xs text-gray-400">(«ضمناً جهت ارتباط با اپراتور عدد سه…»)</span>
              </span>
            </label>
          </div>

          <!-- Live text preview -->
          <div class="mt-6 rounded-xl bg-gray-50 border border-gray-100 p-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-bold uppercase tracking-wider text-gray-400">
                <i class="fas fa-eye"></i> Live text preview
              </span>
              <span v-if="previewing" class="text-xs text-gray-400"><i class="fas fa-spinner fa-spin"></i></span>
            </div>
            <p v-if="preview && preview.text" dir="rtl" class="text-right leading-8 text-gray-800 text-[15px] whitespace-pre-wrap">{{ preview.text }}</p>
            <p v-else class="text-sm text-gray-400 italic">Fill the form to see the assembled text…</p>

            <!-- Missing fragments warning -->
            <div
              v-if="preview && preview.missing && preview.missing.length"
              class="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs flex items-center justify-between gap-2"
            >
              <span>
                <i class="fas fa-triangle-exclamation"></i>
                {{ preview.missing.length }} chunk(s) not on disk — "Build announcement" will
                generate just these via ElevenLabs (≈ ${{ missingCost.toFixed(3) }}) and keep them.
              </span>
              <button type="button" @click="goBuildFromCompose" class="font-semibold underline whitespace-nowrap">
                Build whole categories instead
              </button>
            </div>
          </div>

          <!-- Compose error -->
          <div v-if="composeError" class="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
            <i class="fas fa-exclamation-circle ml-1"></i> {{ composeError }}
          </div>

          <!-- Action -->
          <div class="mt-6 flex items-center justify-end gap-3 flex-wrap">
            <span v-if="preview && preview.fragmentCount" class="text-xs text-gray-400 mr-auto">
              <i class="fas fa-circle-info"></i>
              {{ preview.fragmentCount }} chunks
              <template v-if="missingCost > 0"> · {{ preview.missing.length }} missing ≈ {{ missingCost.toFixed(3) }}$</template>
              <template v-else> · all on disk, free</template>
            </span>
            <button
              type="button"
              @click="randomTest"
              :disabled="composing || !cityOptions.length"
              class="inline-flex items-center gap-2 px-5 h-12 border-2 border-respina-primary text-respina-primary hover:bg-respina-light font-semibold text-sm rounded-xl disabled:opacity-50"
              title="Randomize the form and build it — generates only this announcement's missing chunks via ElevenLabs (a few cents), stores them on disk"
            >
              <i class="fas" :class="composing && randomBusy ? 'fa-spinner fa-spin' : 'fa-dice'"></i>
              Random test (API)
            </button>
            <button
              type="button"
              @click="compose(true)"
              :disabled="composing"
              class="inline-flex items-center gap-2 px-6 h-12 bg-respina-primary hover:bg-respina-secondary text-white font-semibold text-sm rounded-xl shadow-md disabled:opacity-50"
            >
              <i class="fas" :class="composing && !randomBusy ? 'fa-spinner fa-spin' : 'fa-file-audio'"></i>
              {{ composing ? 'Building…' : 'Build announcement' }}
            </button>
          </div>
        </div>

        <!-- Result -->
        <div v-if="result" class="rounded-2xl shadow-lg bg-white border border-gray-100 p-6 md:p-8 mb-8">
          <div class="flex items-center gap-2 text-gray-700 text-sm font-semibold mb-3">
            <i class="fas fa-check-circle text-green-600"></i>
            Announcement ready
            <span v-if="result.cached" class="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">cached</span>
          </div>
          <audio v-if="audioUrl" :src="audioUrl" controls class="w-full mb-4"></audio>
          <p dir="rtl" class="text-right text-sm text-gray-600 leading-7 mb-4 bg-gray-50 rounded-lg p-3">{{ result.text }}</p>
          <div class="flex flex-wrap items-center gap-3">
            <button
              type="button"
              @click="downloadResult"
              class="inline-flex items-center gap-2 px-4 py-2 bg-respina-primary hover:bg-respina-secondary text-white text-sm font-semibold rounded-lg"
            >
              <i class="fas fa-download"></i> Download MP3
            </button>
            <span class="text-xs text-gray-500">
              {{ formatBytes(result.bytes) }} · stitched locally ({{ result.method }})
            </span>
            <span v-if="result.built" class="text-xs text-blue-600">
              <i class="fas fa-cloud-arrow-down"></i>
              {{ result.built.created }} new chunk(s) generated
              ({{ result.built.charsGenerated }} chars ≈ ${{ (result.built.charsGenerated * 0.0001).toFixed(3) }})
              — saved to library
            </span>
          </div>
        </div>
      </section>

      <!-- ============================================================ -->
      <!-- TAB 3 — FREE TEXT                                             -->
      <!-- ============================================================ -->
      <section v-show="tab === 'free'">
        <div class="rounded-2xl shadow-lg bg-white overflow-hidden border border-gray-100 mb-6">
          <form @submit.prevent="synthesize" class="p-6 md:p-8">
            <label class="flex items-center gap-2 text-gray-700 text-sm font-semibold mb-2">
              <i class="fas fa-keyboard text-respina-primary"></i> Free text
            </label>
            <textarea
              ref="textarea"
              v-model="text"
              rows="5"
              maxlength="5000"
              class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-respina-primary focus:ring-2 focus:ring-blue-200 outline-none transition-all resize-y"
              placeholder="Type or paste up to 5000 characters…"
              dir="auto"
            ></textarea>
            <div class="flex items-center justify-between mt-2 gap-2 flex-wrap">
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="t in tags"
                  :key="t"
                  type="button"
                  @click="insertTag(t)"
                  class="px-2 py-1 text-xs rounded-md bg-gray-100 hover:bg-respina-primary hover:text-white text-gray-700 font-mono transition-colors"
                >{{ t }}</button>
              </div>
              <div class="text-xs text-gray-400">{{ text.length }} / 5000</div>
            </div>
            <div class="flex items-center justify-between mt-6">
              <div class="text-xs text-gray-500">
                <i class="fas fa-clock mr-1"></i>
                Free-text files auto-delete after 24 hours.
              </div>
              <button
                type="submit"
                :disabled="busy || !text.trim()"
                class="inline-flex items-center justify-center gap-2 px-6 h-12 bg-respina-primary hover:bg-respina-secondary text-white font-semibold text-sm rounded-xl shadow-md disabled:opacity-60"
              >
                <i class="fas text-sm" :class="busy ? 'fa-spinner fa-spin' : 'fa-microphone-alt'"></i>
                <span>{{ busy ? 'Generating…' : 'Generate Audio' }}</span>
              </button>
            </div>
          </form>
        </div>

        <div v-if="lastResult" class="rounded-2xl shadow-lg bg-white border border-gray-100 mb-8 p-6 md:p-8">
          <div class="flex items-center gap-2 text-gray-700 text-sm font-semibold mb-3">
            <i class="fas fa-check-circle text-green-600"></i> Latest audio ready
          </div>
          <audio :src="freeAudioUrl || lastResult.downloadUrl" controls class="w-full mb-4"></audio>
          <div class="flex flex-wrap items-center gap-3">
            <button
              type="button"
              @click="downloadFree"
              class="inline-flex items-center gap-2 px-4 py-2 bg-respina-primary hover:bg-respina-secondary text-white text-sm font-semibold rounded-lg"
            >
              <i class="fas fa-download"></i> Download MP3
            </button>
            <span class="text-xs text-gray-500">{{ formatBytes(lastResult.bytes) }}</span>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { apiGet, apiPost, apiGetBlob, apiDelete, apiUpload, apiPatch } from "@/lib/http";

const CATEGORY_META = {
  head: { label: "Greeting + intro", icon: "fa-comment-dots" },
  flight: { label: "Flight numbers (10–999)", icon: "fa-hashtag" },
  flight4: { label: "4-digit flights: first pair", icon: "fa-hashtag" },
  pair: { label: "4-digit flights: second pair", icon: "fa-hashtag" },
  date: { label: "Dates (day × month)", icon: "fa-calendar" },
  origin: { label: "Origins", icon: "fa-plane-departure" },
  dest: { label: "Destinations", icon: "fa-plane-arrival" },
  airline: { label: "Airlines", icon: "fa-plane" },
  cancelled: { label: "\"باطل شده و\"", icon: "fa-ban" },
  time: { label: "Times (24h, hour × minute)", icon: "fa-clock" },
  expedite: { label: "\"با تعجیل\" (type 3)", icon: "fa-forward" },
  delay: { label: "\"با تاخیر بیش از دو/سه ساعت\" (type 4)", icon: "fa-clock-rotate-left" },
  tail: { label: "IVR menu tail", icon: "fa-quote-right" },
  operator: { label: "Operator line (optional)", icon: "fa-headset" },
  note: { label: "اطلاع‌رسانی notes (optional)", icon: "fa-circle-info" },
  clock: { label: "Note deadline time (\"ساعت …\")", icon: "fa-hourglass-half" },
};

export default {
  data() {
    return {
      tab: "library",
      tabs: [
        { id: "library", step: 1, label: "Library", icon: "fa-layer-group" },
        { id: "compose", step: 2, label: "Compose", icon: "fa-wand-magic-sparkles" },
        { id: "free", step: 3, label: "Free text", icon: "fa-keyboard" },
      ],
      error: null,

      // library
      manifest: null,
      loadingManifest: false,
      building: false,
      stopFlag: false,
      buildStats: null,
      buildError: null,
      overwrite: false,
      showAdvanced: false,
      cityOptions: [],
      airlineOptions: [],
      missingChars: 0,

      // fragment list/edit modal
      fragModal: null,        // { id, label, icon } when open
      fragItems: [],          // items for the open category
      fragLoading: false,
      fragFilter: "",
      fragOnlyMissing: false,
      fragVisible: 200,
      fragBusyKey: null,      // key of the fragment mid-operation
      fragBusyAction: null,   // 'gen' | 'del' | 'up' | 'save' | 'reset'
      fragPlayingKey: null,
      fragEditKey: null,      // key whose text is being edited
      fragEditText: "",       // edit buffer
      fragAddOpen: false,     // "add airline/city" form open
      fragAddName: "",
      fragAddSpoken: "",      // airline pronunciation (optional)
      fragAddIata: "",        // city IATA code (optional)
      fragAddBusy: false,
      fragError: null,
      fragUploadTarget: null, // item awaiting a picked file
      fragAudioEl: null,
      fragAudioUrl: null,

      // compose
      months: [
        { value: 1, label: "فروردین" }, { value: 2, label: "اردیبهشت" },
        { value: 3, label: "خرداد" }, { value: 4, label: "تیر" },
        { value: 5, label: "مرداد" }, { value: 6, label: "شهریور" },
        { value: 7, label: "مهر" }, { value: 8, label: "آبان" },
        { value: 9, label: "آذر" }, { value: 10, label: "دی" },
        { value: 11, label: "بهمن" }, { value: 12, label: "اسفند" },
      ],
      form: {
        flightNumber: 45, day: 25, month: 12,
        origin: "thr", destination: "mhd", airline: "mahan",
        rFlight: 312, rDay: 26, rMonth: 12, rAirline: "aseman",
        hour: 18, minute: 50,
        operator: true,
      },
      preview: null,
      previewing: false,
      composing: false,
      randomBusy: false,
      composeError: null,
      result: null,
      audioUrl: null,
      previewTimer: null,

      // free text
      text: "",
      busy: false,
      lastResult: null,
      freeAudioUrl: null,
      tags: ["[thoughtful]", "[announcing]", "[calm]", "[excited]", "[pause]"],
    };
  },
  computed: {
    overallPct() {
      if (!this.manifest || !this.manifest.total) return 0;
      return Math.round((this.manifest.existing / this.manifest.total) * 100);
    },
    categoryCards() {
      const bc = this.manifest?.byCategory || {};
      return Object.keys(CATEGORY_META).map((id) => {
        const c = bc[id] || { total: 0, existing: 0 };
        const total = c.total || 0;
        const existing = c.existing || 0;
        return {
          id,
          label: CATEGORY_META[id].label,
          icon: CATEGORY_META[id].icon,
          total,
          existing,
          pct: total ? Math.round((existing / total) * 100) : 0,
          done: total > 0 && existing >= total,
        };
      });
    },
    fragReadyCount() {
      return this.fragItems.filter((i) => i.exists).length;
    },
    fragAddIsCity() {
      return !!this.fragModal && (this.fragModal.id === "origin" || this.fragModal.id === "dest");
    },
    fragCanAdd() {
      return !!this.fragModal && (this.fragModal.id === "airline" || this.fragAddIsCity);
    },
    filteredFrags() {
      const q = this.fragFilter.trim().toLowerCase();
      let list = this.fragItems;
      if (this.fragOnlyMissing) list = list.filter((i) => !i.exists);
      if (q) {
        list = list.filter(
          (i) =>
            String(i.key).toLowerCase().includes(q) ||
            String(i.text || "").toLowerCase().includes(q)
        );
      }
      return list;
    },
    visibleFrags() {
      return this.filteredFrags.slice(0, this.fragVisible);
    },
    missingCost() {
      // Eleven v3 API: $0.10 per 1000 chars — cost of this announcement's
      // not-yet-generated chunks.
      const miss = this.preview?.missing || [];
      const chars = miss.reduce((s, m) => s + [...(m.text || "")].length, 0);
      return (chars * 0.1) / 1000;
    },
    formVars() {
      const f = this.form;
      return {
        flightNumber: f.flightNumber,
        date: { day: f.day, month: f.month },
        origin: f.origin,
        destination: f.destination,
        airline: f.airline,
        replacementFlightNumber: f.rFlight,
        replacementDate: { day: f.rDay, month: f.rMonth },
        replacementAirline: f.rAirline,
        hour: f.hour,
        minute: f.minute,
        operator: f.operator,
      };
    },
  },
  watch: {
    form: { handler() { this.schedulePreview(); }, deep: true },
    tab(v) { if (v === "compose") this.schedulePreview(); },
    fragFilter() { this.fragVisible = 200; },
    fragOnlyMissing() { this.fragVisible = 200; },
  },
  mounted() {
    this.loadManifest();
  },
  beforeUnmount() {
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    if (this.freeAudioUrl) URL.revokeObjectURL(this.freeAudioUrl);
    this.stopFragAudio();
  },
  methods: {
    // ---------- library ----------
    async loadManifest() {
      this.loadingManifest = true;
      try {
        const data = await apiGet("/api/tts/library/manifest");
        this.manifest = {
          total: data.total,
          existing: data.existing,
          missing: data.missing,
          byCategory: data.byCategory || {},
        };
        this.missingChars = (data.items || [])
          .filter((it) => !it.exists)
          .reduce((s, it) => s + [...(it.text || "")].length, 0);
        // Populate dropdown options once (frozen — no need for reactivity).
        // Chunk texts are full phrases ("از مبدا تهران") — strip the carrier
        // prefix so dropdowns show just the name.
        if (!this.cityOptions.length || !this.airlineOptions.length) {
          const cities = [];
          const airlines = [];
          for (const it of data.items || []) {
            if (it.category === "origin") {
              cities.push({ key: it.key, text: it.text.replace(/^از مبدا /, "") });
            } else if (it.category === "airline") {
              airlines.push({
                key: it.key,
                text: it.text.replace(/^هواپیمایی /, "").replace(/ \[thoughtful\]$/, ""),
              });
            }
          }
          this.cityOptions = Object.freeze(cities);
          this.airlineOptions = Object.freeze(airlines);
        }
        if (this.tab === "compose") this.schedulePreview();
      } catch (e) {
        this.error = e?.message ?? "Failed to load library status";
      } finally {
        this.loadingManifest = false;
      }
    },
    async buildMissing(cats) {
      if (this.building) return;
      this.building = true;
      this.stopFlag = false;
      this.buildError = null;
      this.buildStats = { created: 0, remaining: 0, failed: 0, lastText: "" };
      const single = this.overwrite; // overwrite = one bounded pass
      let guard = 0;
      try {
        while (!this.stopFlag && guard++ < 500) {
          const res = await apiPost("/api/tts/library/build", {
            categories: cats || undefined,
            overwrite: this.overwrite,
            limit: single ? 50 : 25,
            concurrency: 3,
          });
          const created = res.created || 0;
          const failed = res.failed || 0;
          this.buildStats.created += created;
          this.buildStats.failed += failed;
          this.buildStats.remaining = res.remaining || 0;
          const lastCreated = (res.results || []).filter((r) => r.status === "created").pop();
          if (lastCreated) this.buildStats.lastText = lastCreated.text;
          // Optimistically update counts
          this.applyBuildResults(res.results || []);

          if (failed > 0 && created === 0) {
            this.buildError = (res.errors && res.errors[0] && res.errors[0].error)
              || "Some fragments failed to generate.";
            break;
          }
          if (single || (res.remaining || 0) === 0) break;
        }
      } catch (e) {
        this.buildError = e?.message ?? "Build failed";
      } finally {
        this.building = false;
        await this.loadManifest(); // reconcile exact numbers
      }
    },
    applyBuildResults(results) {
      if (!this.manifest) return;
      const bc = this.manifest.byCategory;
      for (const r of results) {
        if (r.status !== "created") continue;
        if (bc[r.category]) bc[r.category].existing = Math.min(bc[r.category].existing + 1, bc[r.category].total);
        this.manifest.existing = Math.min(this.manifest.existing + 1, this.manifest.total);
        this.missingChars = Math.max(0, this.missingChars - [...(r.text || "")].length);
      }
      this.manifest.missing = Math.max(0, this.manifest.total - this.manifest.existing);
    },
    estCost(chars) {
      // Eleven v3 API rate: $0.10 per 1000 characters.
      const usd = (Number(chars) || 0) * 0.1 / 1000;
      return "$" + usd.toFixed(2);
    },

    // ---------- fragment list / edit ----------
    async openFragments(cat) {
      this.stopFragAudio();
      this.fragModal = { id: cat.id, label: cat.label, icon: cat.icon };
      this.fragItems = [];
      this.fragFilter = "";
      this.fragOnlyMissing = false;
      this.fragVisible = 200;
      this.fragError = null;
      this.fragAddOpen = false;
      await this.loadFragItems();
    },
    closeFragments() {
      this.stopFragAudio();
      this.fragModal = null;
      this.fragItems = [];
      this.fragUploadTarget = null;
    },
    async loadFragItems() {
      if (!this.fragModal) return;
      this.fragLoading = true;
      try {
        const data = await apiGet(
          `/api/tts/library/manifest?categories=${encodeURIComponent(this.fragModal.id)}`
        );
        this.fragItems = (data.items || []).map((it) => ({
          key: it.key,
          text: it.text,
          exists: !!it.exists,
          bytes: it.bytes,
          overridden: !!it.overridden,
          stale: !!it.stale,
          custom: !!it.custom,
        }));
      } catch (e) {
        this.fragError = e?.message ?? "Failed to load fragments";
      } finally {
        this.fragLoading = false;
      }
    },
    // Keep the category card + overall progress in sync after a single change.
    setFragExists(item, exists) {
      if (item.exists === exists) return;
      const delta = exists ? 1 : -1;
      item.exists = exists;
      const bc = this.manifest && this.manifest.byCategory
        ? this.manifest.byCategory[this.fragModal.id]
        : null;
      if (bc) bc.existing = Math.max(0, Math.min(bc.total, bc.existing + delta));
      if (this.manifest) {
        this.manifest.existing = Math.max(0, Math.min(this.manifest.total, this.manifest.existing + delta));
        this.manifest.missing = Math.max(0, this.manifest.total - this.manifest.existing);
      }
    },
    fragPath(item) {
      return `/api/tts/library/fragment/${this.fragModal.id}/${encodeURIComponent(item.key)}`;
    },
    async genFrag(item) {
      if (this.fragBusyKey) return;
      this.fragBusyKey = item.key;
      this.fragBusyAction = "gen";
      this.fragError = null;
      try {
        await apiPost(this.fragPath(item), { overwrite: true });
        this.setFragExists(item, true);
        item.bytes = null;
        item.stale = false; // audio now matches the current text
      } catch (e) {
        this.fragError = e?.message ?? "Generation failed";
      } finally {
        this.fragBusyKey = null;
        this.fragBusyAction = null;
      }
    },
    // ---- add / remove custom airlines & cities ----
    openAdd() {
      this.fragAddOpen = true;
      this.fragAddName = "";
      this.fragAddSpoken = "";
      this.fragAddIata = "";
      this.fragError = null;
    },
    cancelAdd() {
      this.fragAddOpen = false;
    },
    async saveAdd() {
      const name = this.fragAddName.trim();
      if (!name) return;
      this.fragAddBusy = true;
      this.fragError = null;
      try {
        if (this.fragAddIsCity) {
          await apiPost("/api/tts/library/city", { name, iata: this.fragAddIata.trim() || undefined });
        } else {
          await apiPost("/api/tts/library/airline", { name, spoken: this.fragAddSpoken.trim() || undefined });
        }
        this.fragAddOpen = false;
        await this.loadFragItems();
        this.loadManifest(); // keep the category card counts in sync
      } catch (e) {
        this.fragError = e?.message ?? "Add failed";
      } finally {
        this.fragAddBusy = false;
      }
    },
    async removeCustom(item) {
      if (this.fragBusyKey) return;
      this.fragBusyKey = item.key;
      this.fragBusyAction = "del";
      this.fragError = null;
      try {
        if (this.fragPlayingKey === item.key) this.stopFragAudio();
        const kind = this.fragAddIsCity ? "city" : "airline";
        await apiDelete(`/api/tts/library/${kind}/${encodeURIComponent(item.key)}`);
        this.fragItems = this.fragItems.filter((i) => i.key !== item.key);
        this.loadManifest();
      } catch (e) {
        this.fragError = e?.message ?? "Remove failed";
      } finally {
        this.fragBusyKey = null;
        this.fragBusyAction = null;
      }
    },
    // ---- edit / reset the fragment TEXT (stored in the DB) ----
    startEdit(item) {
      this.fragEditKey = item.key;
      this.fragEditText = item.text;
    },
    cancelEdit() {
      this.fragEditKey = null;
      this.fragEditText = "";
    },
    async saveEdit(item) {
      const text = this.fragEditText.trim();
      if (!text) return;
      this.fragBusyKey = item.key;
      this.fragBusyAction = "save";
      this.fragError = null;
      try {
        const res = await apiPatch(this.fragPath(item), { text });
        item.text = res.text;
        item.overridden = true;
        if (item.exists) item.stale = true; // audio is now behind the text
        this.fragEditKey = null;
        this.fragEditText = "";
      } catch (e) {
        this.fragError = e?.message ?? "Save failed";
      } finally {
        this.fragBusyKey = null;
        this.fragBusyAction = null;
      }
    },
    async resetFrag(item) {
      this.fragBusyKey = item.key;
      this.fragBusyAction = "reset";
      this.fragError = null;
      try {
        const res = await apiPost(`${this.fragPath(item)}/reset`, {});
        item.text = res.text;
        item.overridden = false;
        if (item.exists) item.stale = true; // default text differs from audio
      } catch (e) {
        this.fragError = e?.message ?? "Reset failed";
      } finally {
        this.fragBusyKey = null;
        this.fragBusyAction = null;
      }
    },
    async delFrag(item) {
      if (this.fragBusyKey) return;
      this.fragBusyKey = item.key;
      this.fragBusyAction = "del";
      this.fragError = null;
      try {
        if (this.fragPlayingKey === item.key) this.stopFragAudio();
        await apiDelete(this.fragPath(item));
        this.setFragExists(item, false);
      } catch (e) {
        this.fragError = e?.message ?? "Delete failed";
      } finally {
        this.fragBusyKey = null;
        this.fragBusyAction = null;
      }
    },
    pickUpload(item) {
      this.fragUploadTarget = item;
      const el = this.$refs.fragFile;
      if (el) { el.value = ""; el.click(); }
    },
    async onUploadPicked(e) {
      const file = e.target.files && e.target.files[0];
      const item = this.fragUploadTarget;
      if (!file || !item) return;
      this.fragBusyKey = item.key;
      this.fragBusyAction = "up";
      this.fragError = null;
      try {
        const res = await apiUpload(this.fragPath(item), file);
        this.setFragExists(item, true);
        item.bytes = res?.bytes ?? null;
        item.stale = false; // user supplied the audio directly
      } catch (err) {
        this.fragError = err?.message ?? "Upload failed";
      } finally {
        this.fragBusyKey = null;
        this.fragBusyAction = null;
        this.fragUploadTarget = null;
      }
    },
    async playFrag(item) {
      if (this.fragPlayingKey === item.key) { this.stopFragAudio(); return; }
      this.stopFragAudio();
      this.fragPlayingKey = item.key;
      try {
        const blob = await apiGetBlob(
          `/api/tts/library/file/${this.fragModal.id}/${encodeURIComponent(item.key)}.mp3`
        );
        this.fragAudioUrl = URL.createObjectURL(blob);
        this.fragAudioEl = new Audio(this.fragAudioUrl);
        this.fragAudioEl.onended = () => this.stopFragAudio();
        this.fragAudioEl.onerror = () => this.stopFragAudio();
        await this.fragAudioEl.play();
      } catch (e) {
        this.fragError = e?.message ?? "Playback failed";
        this.stopFragAudio();
      }
    },
    stopFragAudio() {
      if (this.fragAudioEl) { try { this.fragAudioEl.pause(); } catch { /* ignore */ } this.fragAudioEl = null; }
      if (this.fragAudioUrl) { URL.revokeObjectURL(this.fragAudioUrl); this.fragAudioUrl = null; }
      this.fragPlayingKey = null;
    },

    // ---------- compose ----------
    schedulePreview() {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.runPreview(), 350);
    },
    async runPreview() {
      this.composeError = null;
      this.previewing = true;
      try {
        const data = await apiPost("/api/tts/announcement", {
          ...this.formVars,
          dryRun: true,
        });
        this.preview = data;
      } catch (e) {
        this.preview = null;
        this.composeError = e?.message ?? "Invalid input";
      } finally {
        this.previewing = false;
      }
    },
    async compose(build = false) {
      this.composing = true;
      this.composeError = null;
      try {
        const res = await apiPost("/api/tts/announcement", {
          ...this.formVars,
          save: true,
          // build:true → server generates only THIS announcement's missing
          // chunks via ElevenLabs and stores them in the library.
          build: !!build,
        });
        // fetch the saved file (auth-protected) as a blob for playback/download
        const blob = await apiGetBlob(`/api/tts/announcement/file/${res.id}.mp3`);
        if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
        this.audioUrl = URL.createObjectURL(blob);
        this.result = {
          id: res.id,
          bytes: res.bytes,
          method: res.method,
          cached: res.cached,
          text: res.text,
          fragmentCount: res.fragmentCount,
          built: res.built || null,
        };
        if (res.built && res.built.created) this.loadManifest();
      } catch (e) {
        if (e?.status === 409 && e?.data?.missing) {
          this.composeError =
            `${e.data.missing.length} fragment(s) not generated yet: ` +
            e.data.missing.slice(0, 6).map((m) => m.file).join(", ") +
            (e.data.missing.length > 6 ? "…" : "");
        } else {
          this.composeError = e?.message ?? "Failed to build announcement";
        }
      } finally {
        this.composing = false;
        this.randomBusy = false;
      }
    },
    async randomTest() {
      // Randomize the form (new values every click), then build + stitch it.
      const ri = (min, max) => min + Math.floor(Math.random() * (max - min + 1));
      const pick = (arr) => arr[ri(0, arr.length - 1)];
      // flight numbers: mix of 2-, 3- and 4-digit
      const randFlight = () => pick([ri(10, 99), ri(100, 999), ri(1000, 9999)]);
      const origin = pick(this.cityOptions);
      let dest = pick(this.cityOptions);
      while (dest.key === origin.key && this.cityOptions.length > 1) dest = pick(this.cityOptions);
      this.form = {
        flightNumber: randFlight(),
        day: ri(1, 31),
        month: ri(1, 12),
        origin: origin.key,
        destination: dest.key,
        airline: pick(this.airlineOptions).key,
        rFlight: randFlight(),
        rDay: ri(1, 31),
        rMonth: ri(1, 12),
        rAirline: pick(this.airlineOptions).key,
        hour: ri(0, 23),
        minute: ri(0, 59),
        operator: this.form.operator,
      };
      this.randomBusy = true;
      await this.compose(true);
    },
    goBuildFromCompose() {
      const missingCats = [...new Set((this.preview?.missing || []).map((m) => m.category))];
      this.tab = "library";
      this.$nextTick(() => this.buildMissing(missingCats.length ? missingCats : null));
    },
    downloadResult() {
      if (!this.audioUrl) return;
      const a = document.createElement("a");
      a.href = this.audioUrl;
      a.download = `announcement-${this.form.flightNumber}-${this.form.rFlight}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },

    // ---------- free text ----------
    insertTag(tag) {
      const el = this.$refs.textarea;
      const insert = `${tag} `;
      if (!el) { this.text = (this.text + " " + insert).trimStart(); return; }
      const start = el.selectionStart ?? this.text.length;
      const end = el.selectionEnd ?? this.text.length;
      this.text = this.text.slice(0, start) + insert + this.text.slice(end);
      this.$nextTick(() => {
        const pos = start + insert.length;
        el.focus();
        el.setSelectionRange(pos, pos);
      });
    },
    async synthesize() {
      const txt = this.text.trim();
      if (!txt) return;
      this.busy = true;
      this.error = null;
      try {
        const data = await apiPost("/api/tts", { text: txt });
        this.lastResult = data;
        try {
          const blob = await apiGetBlob(`/api/tts/download/${data.id}.mp3`);
          if (this.freeAudioUrl) URL.revokeObjectURL(this.freeAudioUrl);
          this.freeAudioUrl = URL.createObjectURL(blob);
        } catch { /* fall back to downloadUrl */ }
      } catch (e) {
        this.error = e?.message ?? "Failed to generate audio";
      } finally {
        this.busy = false;
      }
    },
    downloadFree() {
      const url = this.freeAudioUrl || this.lastResult?.downloadUrl;
      if (!url) return;
      const a = document.createElement("a");
      a.href = url;
      a.download = `${this.lastResult.id}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    formatBytes(n) {
      const b = Number(n) || 0;
      if (b < 1024) return `${b} B`;
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
      return `${(b / (1024 * 1024)).toFixed(2)} MB`;
    },
  },
};
</script>

<style scoped>
.tts-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.6rem;
  font-size: 0.875rem;
  outline: none;
  transition: border-color 0.15s;
  background: #fff;
}
.tts-input:focus {
  border-color: var(--respina-primary, #ef7c2f);
}

/* ---- shared tokens (fallbacks — global CSS may not define the vars) ---- */
.fl-grid,
.fm-overlay,
.tts-2col {
  --fp: var(--respina-primary, #ef7c2f);
  --fp-dark: var(--respina-secondary, #d9641c);
  --fp-soft: rgba(239, 124, 47, 0.10);
  --fp-soft-2: rgba(239, 124, 47, 0.18);
}

/* ======================= Compose responsive columns ==================== */
.tts-2col {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}
.tts-pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}
@media (min-width: 768px) {
  .tts-2col { grid-template-columns: 1fr 1fr; }
}

/* ============================ Category grid ============================ */
.fl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}
.fl-card {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 1rem;
  padding: 1.1rem 1.15rem 1.15rem;
  box-shadow: 0 4px 16px rgba(17, 24, 39, 0.05);
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}
.fl-card:hover {
  box-shadow: 0 12px 28px rgba(17, 24, 39, 0.10);
  transform: translateY(-2px);
  border-color: #e4e7eb;
}
.fl-card.is-done { border-color: #d6f0dd; }
.fl-card__head {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  text-align: left;
}
.fl-card__icon {
  flex: 0 0 auto;
  width: 2.35rem;
  height: 2.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.7rem;
  background: var(--fp-soft);
  color: var(--fp);
  font-size: 0.95rem;
}
.fl-card__icon.is-done { background: #e7f6ec; color: #16a34a; }
.fl-card__meta {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.fl-card__label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #1f2937;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fl-card__head:hover .fl-card__label { color: var(--fp); }
.fl-card__count { font-size: 0.75rem; color: #9ca3af; }
.fl-card__chev { color: #cbd0d6; font-size: 0.8rem; transition: transform 0.2s, color 0.2s; }
.fl-card__head:hover .fl-card__chev { color: var(--fp); transform: translateX(2px); }
.fl-badge {
  flex: 0 0 auto;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #16a34a;
  background: #e7f6ec;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
}
.fl-progress {
  height: 0.5rem;
  border-radius: 999px;
  background: #f1f3f5;
  overflow: hidden;
}
.fl-progress__bar {
  height: 100%;
  border-radius: 999px;
  background: var(--fp);
  transition: width 0.5s ease;
}
.fl-progress__bar.is-done { background: #22c55e; }
.fl-actions { display: flex; gap: 0.5rem; }
.fl-btn {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 2.25rem;
  padding: 0 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 0.6rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.fl-btn:focus-visible { outline: 2px solid var(--fp); outline-offset: 2px; }
.fl-btn--primary { background: var(--fp-soft); color: var(--fp); }
.fl-btn--primary:hover:not(:disabled) { background: var(--fp); color: #fff; }
.fl-btn--ghost { flex: 0 0 auto; background: #fff; color: #5b6472; border-color: #e4e7eb; }
.fl-btn--ghost:hover { background: #f7f8fa; color: #1f2937; }
.fl-btn:disabled { opacity: 0.5; cursor: default; }

/* ============================ Fragment modal ========================== */
.fm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: clamp(1rem, 4vh, 3rem) 1rem;
  background: rgba(15, 23, 42, 0.55);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
  overflow-y: auto;
  animation: fm-fade 0.16s ease;
}
.fm-panel {
  width: 100%;
  max-width: 720px;
  margin: auto;
  display: flex;
  flex-direction: column;
  max-height: 86vh;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.32);
  overflow: hidden;
  animation: fm-pop 0.18s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}
.fm-header {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #eef0f3;
}
.fm-header__icon {
  flex: 0 0 auto;
  width: 2.35rem;
  height: 2.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.7rem;
  background: var(--fp-soft);
  color: var(--fp);
}
.fm-header__meta { min-width: 0; flex: 1 1 auto; }
.fm-header__title {
  font-weight: 700;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fm-header__sub { font-size: 0.75rem; color: #9ca3af; }
.fm-header__sub b { color: var(--fp); }
.fm-close {
  flex: 0 0 auto;
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 0.55rem;
  border: 0;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background 0.15s, color 0.15s;
}
.fm-close:hover { background: #f3f4f6; color: #374151; }
.fm-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #eef0f3;
  flex-wrap: wrap;
}
.fm-search {
  position: relative;
  flex: 1 1 200px;
  min-width: 160px;
}
.fm-search i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #c3c8cf;
  font-size: 0.75rem;
  pointer-events: none;
}
.fm-search input {
  width: 100%;
  height: 2.25rem;
  padding: 0 0.75rem 0 2rem;
  font-size: 0.85rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.6rem;
  outline: none;
  transition: border-color 0.15s;
}
.fm-search input:focus { border-color: var(--fp); }
.fm-check {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #6b7280;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.fm-check input { accent-color: var(--fp); width: 0.95rem; height: 0.95rem; }
.fm-error {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0.75rem 1.25rem 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.6rem;
}
.fm-error button { border: 0; background: none; color: #ef4444; cursor: pointer; font-size: 1rem; line-height: 1; }
.fm-body { flex: 1 1 auto; overflow-y: auto; padding: 0.5rem 1.25rem 0.75rem; }
.fm-state { padding: 3rem 0; text-align: center; color: #9ca3af; font-size: 0.85rem; }
.fm-list { list-style: none; margin: 0; padding: 0; }
.fm-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.25rem;
  border-bottom: 1px solid #f1f3f5;
}
.fm-row:last-child { border-bottom: 0; }
.fm-dot { flex: 0 0 auto; width: 0.55rem; height: 0.55rem; border-radius: 999px; }
.fm-dot.is-on { background: #22c55e; box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.14); }
.fm-dot.is-off { background: #d1d5db; }
.fm-row__text { min-width: 0; flex: 1 1 auto; }
.fm-row__phrase {
  font-size: 0.9rem;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fm-row.is-missing .fm-row__phrase { color: #9aa1ab; }
.fm-row__key { font-size: 0.7rem; color: #b0b6be; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.fm-row__actions { flex: 0 0 auto; display: flex; align-items: center; gap: 0.25rem; }
.fm-iconbtn {
  width: 2.05rem;
  height: 2.05rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 0.55rem;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 0.15s, color 0.15s;
}
.fm-iconbtn:hover:not(:disabled) { background: var(--fp-soft); color: var(--fp); }
.fm-iconbtn.is-active { background: var(--fp-soft-2); color: var(--fp); }
.fm-iconbtn--blue:hover:not(:disabled) { background: #eaf1ff; color: #2563eb; }
.fm-iconbtn--red:hover:not(:disabled) { background: #fdecec; color: #dc2626; }
.fm-iconbtn--green { color: #16a34a; }
.fm-iconbtn--green:hover:not(:disabled) { background: #e7f6ec; color: #15803d; }
.fm-iconbtn:disabled { opacity: 0.4; cursor: default; }

/* inline text editor + override badges */
.fm-edit {
  width: 100%;
  padding: 0.4rem 0.55rem;
  font-size: 0.9rem;
  font-family: inherit;
  line-height: 1.5;
  border: 2px solid var(--fp);
  border-radius: 0.5rem;
  outline: none;
  resize: vertical;
}
.fm-tag {
  display: inline-block;
  margin-inline-start: 0.4rem;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  vertical-align: middle;
  color: #2563eb;
  background: #eaf1ff;
}
.fm-tag--warn { color: #b45309; background: #fef3c7; }
.fm-tag--new { color: #15803d; background: #e7f6ec; }

/* add airline / city */
.fm-addbtn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 2.25rem;
  padding: 0 0.7rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--fp);
  background: var(--fp-soft);
  border: 0;
  border-radius: 0.6rem;
  cursor: pointer;
  white-space: nowrap;
}
.fm-addbtn:hover { background: var(--fp); color: #fff; }
.fm-add {
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #eef0f3;
  background: #fafbfc;
}
.fm-add__row { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
.fm-add__input {
  flex: 1 1 160px;
  height: 2.25rem;
  padding: 0 0.7rem;
  font-size: 0.85rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.55rem;
  outline: none;
}
.fm-add__input:focus { border-color: var(--fp); }
.fm-add__actions { display: flex; gap: 0.5rem; }
.fm-btn--primary { color: #fff; background: var(--fp); border-color: var(--fp); }
.fm-btn--primary:hover:not(:disabled) { background: var(--fp-dark); }
.fm-btn--primary:disabled { opacity: 0.5; cursor: default; }
.fm-loadmore {
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--fp);
  background: transparent;
  border: 1px dashed #e5e7eb;
  border-radius: 0.6rem;
  cursor: pointer;
}
.fm-loadmore:hover { background: var(--fp-soft); border-color: transparent; }
.fm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #eef0f3;
  font-size: 0.75rem;
  color: #9ca3af;
}
.fm-btn {
  padding: 0 1rem;
  height: 2.25rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 0.6rem;
  cursor: pointer;
  transition: background 0.15s;
}
.fm-btn:hover { background: #f7f8fa; }

@keyframes fm-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes fm-pop { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .fm-overlay, .fm-panel { animation: none; }
  .fl-card { transition: none; }
}
</style>
